import {groupBy} from 'lodash';
import {ILocale} from './../../utilis/ResponseObjects.types';
import config from '../../../config/config';
import toSingleElementArray from '../../../utilis/toSingleElementArray';
import {ISearchDocumentsResponseType, ISingleDocument} from '../../utilis/ResponseObjects.types';

interface ICompactDocumentDocumentType {
  detailedDocumentType: string;
  locale: string;
  tags: string[];
}

const getLocaleSecondPart = (locale: string) => (
  locale.split('_').pop()?.toLowerCase()
)

const getLocaleFirstPart = (locale: string) => (
  locale.slice(0,2).toLowerCase()
)

const getLocalesFromDocuments = (documents: ICompactDocumentDocumentType[]) => (
  documents.map(document => ({locale: document.locale, tagSet: document.tags}))
)

// Filter locale using full locale code
const filterLocalesByFullLocale = (allLocales: ILocale[]) => (
  (locale: ILocale, index: number) => (
    allLocales.findIndex(d => d.locale === locale.locale) === index
  )
)

// Filter locale using only first part of locale code
const filterLocalesByFirstPartLocale = (allLocales: ILocale[]) => (
  (locale: ILocale, index: number) => (
    allLocales.findIndex(d => getLocaleFirstPart(d.locale) === getLocaleFirstPart(locale.locale)) === index
  )
)

// Show only those document's that has required tags, or does not have any tag's at all
const filterDocumentTags = (document: ICompactDocumentDocumentType) => (
  config.tagsConstraints.every(constraint => document.tags.includes(constraint)) || document.tags.length === 0
)


// Show unique locales for each document, preferably with tags
const getLocalesUniqueByTag = (locales: ILocale[]) => {
  const sortedLocales = locales.sort((l1, l2) => l2.tagSet.length - l1.tagSet.length);

  return sortedLocales.filter(filterLocalesByFullLocale(sortedLocales));
}

// Show unique locales for each document, removing non '_CH' ones if needed
const getLocalesUniqueByCode = (locales: ILocale[]) => {
  const sortedLocales = locales.sort((l1, l2) => +(getLocaleSecondPart(l1.locale) === 'CH') - +(getLocaleSecondPart(l2.locale) === 'CH'));

  return sortedLocales.filter(filterLocalesByFirstPartLocale(sortedLocales));
}

const getUniqueLocales = (documents: ICompactDocumentDocumentType[]) => {
  const locales = getLocalesFromDocuments(documents);

  const uniqueByTag = getLocalesUniqueByTag(locales);

  const uniqueByTagAndCode = getLocalesUniqueByCode(uniqueByTag);

  return uniqueByTagAndCode;
}

const searchDocumentResponseTransformer = (result: ISearchDocumentsResponseType): ISingleDocument[] => {
  const documents = [] as ISingleDocument[];

  const compactDocuments = toSingleElementArray(result.hit ?? []).map(hit => ({
    id: hit.productId,
    isin: hit.isin,
    name: hit.name,
    documentTypes: groupBy(
      toSingleElementArray(hit.documentTypes.documentType ?? []).map(document => ({
        detailedDocumentType: document.detailedDocumentType,
        locale: document.documentLocale,
        tags: toSingleElementArray(document.tagSet.tag || [])
      })),
      'detailedDocumentType'
    )
  }));

  compactDocuments.forEach(item => {
    for (const [key, value] of Object.entries(item.documentTypes)) {
      const documentsToAdd = value.filter(filterDocumentTags);

      if (documentsToAdd.length > 0) {
        documents.push({
          id: item.id,
          name: item.name,
          isin: item.isin,
          type: key,
          locales: getUniqueLocales(documentsToAdd),
        });
      }

    }
  })
  
  return documents;
}

export default searchDocumentResponseTransformer;
